import { Navigate, Route, Routes, useLocation } from 'react-router';
import { Coquille } from './components/Coquille.tsx';
import { PorteConfirmation } from './components/PorteConfirmation.tsx';
import { Message } from '@journal/ui';
import { Connexion } from './pages/Connexion.tsx';
import { Abonnement } from './pages/Abonnement.tsx';
import { Accueil } from './pages/Accueil.tsx';
import { EmploiDuTemps } from './pages/EmploiDuTemps.tsx';
import { Journal } from './pages/Journal.tsx';
import { FichesPreparation } from './pages/FichesPreparation.tsx';
import { Impressions } from './pages/Impressions.tsx';
import { Evaluation } from './pages/Evaluation.tsx';
import { HeuresService } from './pages/HeuresService.tsx';
import { Programmation } from './pages/Programmation.tsx';
import { Programme } from './pages/Programme.tsx';
import { Agenda } from './pages/Agenda.tsx';
import { Progressions } from './pages/Progressions.tsx';
import { Equipe } from './pages/Equipe.tsx';
import { Reglages } from './pages/Reglages.tsx';
import { Securite } from './pages/Securite.tsx';
import { Inscription } from './pages/Inscription.tsx';
import { Offres } from './pages/Offres.tsx';
import { EspaceSupprime } from './pages/EspaceSupprime.tsx';
import { Confidentialite, ConditionsGenerales, MentionsLegales } from './pages/Legal.tsx';
import { Contact } from './pages/Contact.tsx';
import { Support, FilSupport } from './pages/Support.tsx';
import { Documentation, DocumentationPublique } from './pages/Documentation.tsx';
import {
  ConfirmationInscription, Invitation, MotDePasseOublie, Reinitialisation,
  VerificationEmail,
} from './pages/ParcoursCompte.tsx';
import { useSession } from './lib/session.tsx';
import { FournisseurAnnee } from './lib/annee.tsx';
import { FournisseurDroits } from './lib/droits.tsx';

export function App() {
  const { etat } = useSession();
  const emplacement = useLocation();

  // Trois états, pas deux : sans « vérification », l'écran de connexion
  // clignoterait à chaque rechargement pour un utilisateur déjà connecté.
  if (etat === 'verification') {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg)]">
        <Message>Chargement…</Message>
      </div>
    );
  }

  return (
    <Routes>
      {/*
        * Écrans publics, déclarés dans les DEUX états de session.
        * Une invitation reçue sur une autre adresse, ou un lien de vérification
        * ouvert depuis la messagerie, arrivent souvent alors qu'une session est
        * déjà ouverte : rediriger dans ce cas ferait échouer l'action sans rien
        * expliquer.
        */}
      {/* Publique : c'est la seule page qu'un directeur puisse consulter
          avant de décider s'il crée un espace. */}
      <Route path="/offres" element={<Offres />} />
      {/*
        * Pages légales : publiques, et accessibles CONNECTÉ comme DÉCONNECTÉ.
        * Les déclarer ici, hors des deux états de session, évite qu'un client
        * abonné soit renvoyé à l'accueil en cherchant les conditions de vente
        * qu'il vient d'accepter.
        */}
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/cgv" element={<ConditionsGenerales />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      {/* Publique et sans session, comme les pages légales : celui qui écrit
          ici le fait précisément parce qu'il n'arrive pas à entrer. */}
      <Route path="/contact" element={<Contact />} />
      <Route path="/inscription" element={<Inscription />} />
      <Route path="/inscription/confirmation" element={<ConfirmationInscription />} />
      <Route path="/verifier-email" element={<VerificationEmail />} />
      <Route path="/mot-de-passe-oublie" element={<MotDePasseOublie />} />
      <Route path="/reinitialiser" element={<Reinitialisation />} />
      <Route path="/rejoindre" element={<Invitation />} />
      {/* Publique : la suppression vient de fermer toutes les sessions. */}
      <Route path="/espace-supprime" element={<EspaceSupprime />} />

      {etat === 'deconnecte' ? (
        <>
          <Route path="/" element={<Connexion />} />
          {/* La documentation se lit aussi SANS compte : on veut voir
              comment l'outil s'utilise avant de s'inscrire. Mêmes adresses
              qu'une fois connecté, où elle vit dans la coquille. */}
          <Route path="/documentation" element={<DocumentationPublique />} />
          <Route path="/documentation/:module" element={<DocumentationPublique />} />
          {/*
            * Toute autre adresse ramène à la connexion en MÉMORISANT la
            * destination : après authentification, l'utilisateur retrouve la
            * page qu'il visait. Le renvoyer à l'accueil lui ferait perdre le
            * lien qu'on venait de lui partager.
            */}
          <Route
            path="*"
            element={
              <Navigate
                to="/"
                replace
                state={{ destination: emplacement.pathname + emplacement.search }}
              />
            }
          />
        </>
      ) : (
        <Route
          element={
            // L'année active est chargée une fois pour toute la coquille :
            // chaque écran métier en dépend, et la recharger par écran
            // multiplierait les requêtes pour une donnée qui ne bouge pas.
            /*
             * La porte de confirmation ENVELOPPE les fournisseurs.
             *
             * Ils interrogent tous les deux des routes métier, qui refusent
             * désormais un compte dont l'adresse n'est pas confirmée. Placée à
             * l'intérieur, la porte s'afficherait derrière deux échecs de
             * chargement — et le message utile arriverait après le bruit.
             */
            <PorteConfirmation>
            <FournisseurAnnee>
              {/* Les droits de l'offre suivent la même logique : ils ne
                  changent pas pendant qu'on travaille, et les redemander par
                  écran ferait clignoter les menus. */}
              <FournisseurDroits>
                <Coquille />
              </FournisseurDroits>
            </FournisseurAnnee>
            </PorteConfirmation>
          }
        >
          {/* L'accueil répond à « qu'est-ce que je fais aujourd'hui ». Y
              rediriger vers le cahier journal ferait perdre cette vue
              d'ensemble à chaque connexion. */}
          <Route path="/" element={<Accueil />} />
          <Route path="/abonnement" element={<Abonnement />} />
          <Route path="/equipe" element={<Equipe />} />
          <Route path="/securite" element={<Securite />} />
          <Route path="/reglages" element={<Reglages />} />
          {/* Support et documentation vivent DANS la coquille : on y arrive
              depuis l'application, et l'on doit pouvoir en revenir sans se
              reconnecter. */}
          <Route path="/support" element={<Support />} />
          <Route path="/support/:id" element={<FilSupport />} />
          <Route path="/documentation" element={<Documentation />} />
          <Route path="/documentation/:module" element={<Documentation />} />
          <Route path="/journal" element={<Journal />} />
          <Route path="/fiches-preparation" element={<FichesPreparation />} />
          <Route path="/impressions" element={<Impressions />} />
          <Route path="/agenda" element={<Agenda />} />
          <Route path="/emploi-du-temps" element={<EmploiDuTemps />} />
          <Route path="/progressions" element={<Progressions />} />
          <Route path="/programmation" element={<Programmation />} />
          <Route path="/evaluation" element={<Evaluation />} />
          <Route path="/heures" element={<HeuresService />} />
          <Route path="/programme" element={<Programme />} />
          {/* Tous les onglets de `MODULES` ont désormais leur écran : plus
              aucun repli « pas encore porté ». Adresse inconnue une fois
              connecté : retour à l'accueil plutôt qu'une coquille vide. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      )}
    </Routes>
  );
}
